import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { orderBy, where } from 'firebase/firestore'
import { useAuth } from '../lib/auth'
import { useCollection } from '../lib/firestore'
import { useTourAllowance } from '../lib/entitlements'
import { useArtist, useArtists, usePeople } from '../lib/artists'
import { useTours } from '../lib/tours'
import { useIsMobile } from '../lib/useMediaQuery'
import { addCrewMember, createCrewMemberByName, createShow, createTour } from '../lib/mutations'
import type { CrewDraft, ShowDraft, TourDraft } from '../lib/mutations'
import { toDateInputValue } from '../lib/zonedTime'
import { formatShortDate } from '../lib/format'
import type { Show, Tour, TourCrew, WithId } from '../types/models'
import { CrewFields, InviteResult, initialCrewDraft } from '../forms/CrewFields'
import { ShowFields, initialShowDraft, showIsValid } from '../forms/ShowFields'
import { ErrorText, Field, Row, Text } from '../components/Form'
import ImageDrop from '../components/ImageDrop'
import Poster from '../components/Poster'
import Icon from '../components/Icon'

/* ============================================================================
   New tour — three steps, in the page.

   A wizard belongs in the page, not in a modal. It is the longest task in the
   product, it creates real records at every step, and a dialog says "this is a
   quick aside you can dismiss" about something that is neither quick nor
   dismissable once step one has written a tour.

   Mirrors `Views/NewTourFlowView.swift`. Three steps rather than one long page
   because each asks one thing, and Crew and Shows can be skipped — someone who
   only wants dates on a calendar is done after step one.

   The tour is created at the END of step one. Steps two and three write
   against a real tour id, which is why Back stops being available once it
   exists: there is nothing to go back to that hasn't already happened.
   ============================================================================ */

const STEPS = [
  { title: 'New tour', blurb: 'Name it and set the dates.' },
  { title: 'Add crew', blurb: "Invite the people coming with you. You can do this later." },
  { title: 'Add shows', blurb: "Add the dates you're playing. You can do this later." },
] as const

export default function NewTourFlow() {
  const { user, uid } = useAuth()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { tours } = useTours(uid ?? undefined)
  const allowance = useTourAllowance(tours, uid ?? undefined)

  const [step, setStep] = useState(0)
  const [tour, setTour] = useState<WithId<Tour> | null>(null)
  const [artistId, setArtistId] = useState<string | null>(null)
  /** The tour's start date as an input string, so step three can open on it.
   *  Held separately because the tour object above is assembled from the
   *  draft and carries strings where a real Tour carries Timestamps. */
  const [startDate, setStartDate] = useState('')

  const current = STEPS[step]

  if (!uid) return null

  // Someone who deep-links here on a full plan gets the same answer the button
  // gives, rather than a form that fails on save.
  if (!allowance.allowed && !tour) {
    return (
      <FlowFrame step={0} title="New tour" blurb="">
        <div className="panel panel-pad stack" style={{ gap: 10, borderColor: 'var(--accent)' }}>
          <h2 className="t-display-s">Plan is full</h2>
          <p className="t-caption" style={{ maxWidth: '60ch' }}>
            {allowance.message} Everything you've already made stays readable and exportable.
          </p>
          <button className="btn btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={() => navigate('/tours')}>
            Back to tours
          </button>
        </div>
      </FlowFrame>
    )
  }

  return (
    <FlowFrame step={step} title={current.title} blurb={current.blurb} compact={isMobile}>
      {step === 0 && (
        <DetailsStep
          ownerId={uid}
          onCreated={(created, draftStart, artist) => {
            setTour(created)
            setStartDate(draftStart)
            setArtistId(artist)
            setStep(1)
          }}
          onCancel={() => navigate('/tours')}
        />
      )}

      {step === 1 && tour && (
        <CrewStep
          tour={tour}
          artistId={artistId}
          inviter={{ id: uid, name: user?.displayName ?? 'An Encore user' }}
          onNext={() => setStep(2)}
          onSkip={() => setStep(2)}
        />
      )}

      {step === 2 && tour && (
        <ShowsStep
          tour={tour}
          userId={uid}
          defaultDate={startDate}
          onBack={() => setStep(1)}
          onFinish={() => navigate(`/tours/${tour.id}`)}
        />
      )}
    </FlowFrame>
  )
}

/* ------------------------------------------------------------------- frame */

function FlowFrame({
  step,
  title,
  blurb,
  compact,
  children,
}: {
  step: number
  title: string
  blurb: string
  compact?: boolean
  children: React.ReactNode
}) {
  return (
    <div style={{ padding: compact ? '20px var(--gutter) 120px' : '30px var(--gutter) 80px' }}>
      <div className="sections" style={{ maxWidth: 980, margin: '0 auto', gap: 20 }}>
        <header className="stack" style={{ gap: 6 }}>
          <span className="t-eyebrow" style={{ color: 'var(--accent-text)' }}>
            Step {step + 1} of {STEPS.length}
          </span>
          <h1 className={compact ? 't-display-m' : 't-display-l'}>{title}</h1>
          {blurb && <p className="t-caption">{blurb}</p>}
        </header>
        {children}
      </div>
    </div>
  )
}

/**
 * The bar at the bottom of every step after the first.
 *
 * Skip is quiet and Next is loud, because the only real signal is whether the
 * step was engaged with — nobody should feel steered into inviting crew they
 * haven't hired yet.
 */
function StepFooter({
  nextLabel,
  onBack,
  onSkip,
  onNext,
  busy,
}: {
  nextLabel: string
  onBack?: () => void
  onSkip: () => void
  onNext: () => void
  busy?: boolean
}) {
  return (
    <div className="spread" style={{ gap: 12, flexWrap: 'wrap' }}>
      {onBack ? (
        <button className="btn btn-secondary" onClick={onBack} disabled={busy}>
          Back
        </button>
      ) : (
        <span />
      )}
      <div className="row" style={{ gap: 14 }}>
        <button className="btn btn-quiet" onClick={onSkip} disabled={busy}>
          Skip for now
        </button>
        <button className="btn btn-primary" onClick={onNext} disabled={busy}>
          {nextLabel}
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- step 1: tour */

function DetailsStep({
  ownerId,
  onCreated,
  onCancel,
}: {
  ownerId: string
  onCreated: (tour: WithId<Tour>, startDate: string, artistId: string | null) => void
  onCancel: () => void
}) {
  const today = toDateInputValue(new Date())
  const [draft, setDraft] = useState<TourDraft>({
    tourName: '',
    artist: '',
    startDate: today,
    endDate: today,
    posterURL: '',
  })
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /**
   * A one-off date is still a tour underneath.
   *
   * Shows hang off a tour in the schema on all three platforms, so a standalone
   * date is a tour of one — same records, same itinerary, same day sheet. What
   * changes is the language, because nobody calls a Tuesday in Melbourne a
   * tour, and being made to name one is the reason people bounce off this
   * screen.
   */
  const [kind, setKind] = useState<'tour' | 'show'>('tour')
  const isShow = kind === 'show'
  const { artists, isRoster } = useArtists()
  const [params] = useSearchParams()
  // Arriving from an artist's page, the artist is already decided.
  const requested = params.get('artist')

  const patch = (next: Partial<TourDraft>) => setDraft((previous) => ({ ...previous, ...next }))

  // With one artist there is nothing to choose, so the picker stays hidden and
  // the tour is stamped silently. A solo user should never learn the word
  // "artist profile" to make a tour.
  const artistId = draft.artistId ?? requested ?? artists[0]?.id ?? null
  const chosen = artists.find((entry) => entry.id === artistId)

  // Fill the artist name in from the roster once, so someone coming from an
  // artist page doesn't retype what they just clicked.
  useEffect(() => {
    if (chosen && !draft.artist) patch({ artist: chosen.name })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen?.id])

  const valid =
    draft.tourName.trim().length > 0 &&
    draft.artist.trim().length > 0 &&
    draft.startDate.length > 0 &&
    (isShow || (draft.endDate.length > 0 && draft.endDate >= draft.startDate))

  async function create() {
    if (!valid) return
    setBusy(true)
    setError(null)
    try {
      // A single date is a run of one, so both ends are the same day.
      const dates = isShow ? { endDate: draft.startDate } : {}
      const id = await createTour({ ...draft, ...dates, artistId }, ownerId)
      // Enough of a Tour for steps two and three, which only need the id and
      // the name. Re-reading it from Firestore here would be a round trip for
      // fields nothing downstream reads.
      onCreated(
        { id, ...draft, ...(isShow ? { endDate: draft.startDate } : {}) } as unknown as WithId<Tour>,
        draft.startDate,
        artistId,
      )
    } catch (caught) {
      setError((caught as Error).message)
      setBusy(false)
    }
  }

  return (
    <>
      <div
        style={{
          display: 'grid',
          gap: 20,
          // The poster sits beside the fields on a wide screen and above them
          // on a narrow one — it is the thing people recognise a tour by, so
          // it never gets pushed below the fold.
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'start',
        }}
        className="responsive-grid"
      >
        <div className="panel panel-pad stack" style={{ gap: 14 }}>
          {/* First decision, because it renames everything under it. */}
          <div
            className="row"
            style={{
              gap: 3,
              padding: 3,
              borderRadius: 999,
              background: 'var(--panel-raised)',
              alignSelf: 'flex-start',
            }}
          >
            {(['tour', 'show'] as const).map((option) => {
              const on = option === kind
              return (
                <button
                  key={option}
                  type="button"
                  className="chip"
                  aria-pressed={on}
                  onClick={() => setKind(option)}
                  style={{
                    cursor: 'pointer',
                    border: 0,
                    background: on ? 'var(--panel)' : 'transparent',
                    color: on ? 'var(--accent-text)' : 'var(--text-secondary)',
                    boxShadow: on ? 'var(--shadow-card)' : undefined,
                  }}
                >
                  {option === 'tour' ? 'A tour' : 'One show'}
                </button>
              )
            })}
          </div>

          <Field label="Artist">
            <Text
              value={draft.artist}
              onChange={(value) => patch({ artist: value })}
              autoFocus
            />
          </Field>

          {isRoster && (
            <Field label="Artist profile" hint="Which act on your roster this run belongs to.">
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {artists.map((entry) => {
                  const on = artistId === entry.id
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      className="chip"
                      aria-pressed={on}
                      onClick={() => patch({ artistId: entry.id, artist: entry.name })}
                      style={{
                        cursor: 'pointer',
                        background: on ? 'var(--accent-wash)' : 'transparent',
                        border: `var(--hairline-width) solid ${
                          on ? 'var(--accent)' : 'var(--hairline)'
                        }`,
                        color: on ? 'var(--accent-text)' : 'var(--text-secondary)',
                      }}
                    >
                      {entry.name}
                    </button>
                  )
                })}
              </div>
            </Field>
          )}

          <Field label={isShow ? 'Show name' : 'Tour name'}>
            <Text
              value={draft.tourName}
              onChange={(value) => patch({ tourName: value })}
            />
          </Field>

          {/* One date needs one field. Asking for an end date on a Tuesday in
              Melbourne is the kind of small nonsense that makes a form feel
              like it was written for somebody else. */}
          {isShow ? (
            <Field label="Date">
              <Text
                type="date"
                value={draft.startDate}
                onChange={(value) => patch({ startDate: value, endDate: value })}
              />
            </Field>
          ) : (
            <>
              <Row>
                <Field label="Start date">
                  <Text
                    type="date"
                    value={draft.startDate}
                    onChange={(value) => patch({ startDate: value })}
                  />
                </Field>
                <Field label="End date">
                  <Text
                    type="date"
                    value={draft.endDate}
                    onChange={(value) => patch({ endDate: value })}
                  />
                </Field>
              </Row>

              <p className="t-caption">
                Include travel and production days — you can change these later.
              </p>

              {draft.endDate && draft.startDate && draft.endDate < draft.startDate && (
                <ErrorText>The tour can't end before it starts.</ErrorText>
              )}
            </>
          )}

          <ImageDrop
            value={draft.posterURL}
            onChange={(url) => patch({ posterURL: url })}
            onBusyChange={setUploading}
          />

          <ErrorText>{error}</ErrorText>
        </div>

        <div style={{ width: 200 }}>
          <Poster url={draft.posterURL} width={200} alt={draft.tourName || 'New tour poster'} />
        </div>
      </div>

      <div className="spread" style={{ gap: 12 }}>
        <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={() => void create()}
          disabled={!valid || busy || uploading}
        >
          {uploading ? 'Uploading…' : busy ? 'Creating…' : 'Next'}
        </button>
      </div>
    </>
  )
}

/* ------------------------------------------------------------- step 2: crew */

function CrewStep({
  tour,
  artistId,
  inviter,
  onNext,
  onSkip,
}: {
  tour: WithId<Tour>
  artistId: string | null
  inviter: { id: string; name: string }
  onNext: () => void
  onSkip: () => void
}) {
  const crew = useCollection<TourCrew>('tourCrew', [where('tourId', '==', tour.id)])
  const { artist } = useArtist(artistId ?? undefined)
  const { orgId } = useArtists()
  const people = usePeople(orgId)
  const [adding, setAdding] = useState(false)
  const [seeding, setSeeding] = useState(false)

  // The artist's usual crew, minus anyone already on this tour. This is the
  // whole point of a touring party: entering someone once should take them
  // out of every future setup, not just record that they exist.
  const suggested = (artist?.defaultTouringParty ?? [])
    .filter((member) => member.defaultIncluded)
    .map((member) => people.find((person) => person.id === member.personId))
    .filter((person): person is (typeof people)[number] => Boolean(person))
    .filter((person) => !crew.data.some((member) => member.name === person.name))

  return (
    <>
      {suggested.length > 0 && (
        <section className="stack" style={{ gap: 8 }}>
          <h2 className="t-eyebrow">{artist?.name}'s touring party</h2>
          <div className="panel panel-pad stack" style={{ gap: 12 }}>
            <p className="t-caption">
              {suggested.length} {suggested.length === 1 ? 'person' : 'people'} normally travel
              with this act. Add them and they're on the tour as viewers — invite them when
              you're ready.
            </p>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              {suggested.map((person) => (
                <span key={person.id} className="chip">
                  {person.name}
                </span>
              ))}
            </div>
            <button
              className="btn btn-primary"
              style={{ alignSelf: 'flex-start' }}
              disabled={seeding}
              onClick={async () => {
                setSeeding(true)
                try {
                  for (const person of suggested) {
                    await createCrewMemberByName(person.name, tour.id, inviter.id, person.id)
                  }
                } finally {
                  setSeeding(false)
                }
              }}
            >
              {seeding ? 'Adding…' : `Add all ${suggested.length}`}
            </button>
          </div>
        </section>
      )}

      {crew.data.length > 0 && (
        <section className="stack" style={{ gap: 8 }}>
          <h2 className="t-eyebrow">On this tour</h2>
          <div className="panel rows">
            {crew.data.map((member) => (
              <div key={member.id} className="row" style={{ padding: '12px 16px', gap: 12 }}>
                <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                  <span className="truncate" style={{ fontWeight: 500 }}>
                    {member.name}
                  </span>
                  <span className="t-caption truncate">
                    {member.email}
                    {member.roles?.length ? ` · ${member.roles.join(', ')}` : ''}
                  </span>
                </span>
                <span className={member.status === 'accepted' ? 'chip chip-positive' : 'chip'}>
                  {member.status === 'accepted' ? 'Accepted' : 'Invited'}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="stack" style={{ gap: 8 }}>
        <h2 className="t-eyebrow">New crew member</h2>
        {adding ? (
          <CrewForm
            tour={tour}
            inviter={inviter}
            onDone={() => setAdding(false)}
          />
        ) : (
          <button
            className="panel panel-pad row"
            style={{
              gap: 10,
              width: '100%',
              cursor: 'pointer',
              borderStyle: 'dashed',
              justifyContent: 'center',
              color: 'var(--accent-text)',
            }}
            onClick={() => setAdding(true)}
          >
            <Icon name="plus" size={16} />
            Add someone
          </button>
        )}
      </section>

      <StepFooter nextLabel="Next" onSkip={onSkip} onNext={onNext} />
    </>
  )
}

function CrewForm({
  tour,
  inviter,
  onDone,
}: {
  tour: WithId<Tour>
  inviter: { id: string; name: string }
  onDone: () => void
}) {
  const [draft, setDraft] = useState<CrewDraft>(() => initialCrewDraft())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ joiningCode: string | null; existing: boolean } | null>(
    null,
  )

  const patch = (next: Partial<CrewDraft>) => setDraft((previous) => ({ ...previous, ...next }))
  const valid = draft.name.trim().length > 0

  if (result) {
    return (
      <div className="panel panel-pad stack" style={{ gap: 14 }}>
        <InviteResult
          name={draft.name}
          email={draft.email}
          tourName={tour.tourName}
          joiningCode={result.joiningCode}
          existing={result.existing}
        />
        <button className="btn btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={onDone}>
          Add another
        </button>
      </div>
    )
  }

  return (
    <div className="panel panel-pad stack" style={{ gap: 12 }}>
      <CrewFields draft={draft} patch={patch} isNew autoFocus={false} />
      <ErrorText>{error}</ErrorText>
      <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={onDone} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={!valid || busy}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              const outcome = await addCrewMember(draft, tour, inviter)
              setResult({ joiningCode: outcome.joiningCode, existing: !!outcome.existingUserId })
            } catch (caught) {
              setError((caught as Error).message)
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? 'Inviting…' : 'Add & invite'}
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------ step 3: shows */

function ShowsStep({
  tour,
  userId,
  defaultDate,
  onBack,
  onFinish,
}: {
  tour: WithId<Tour>
  userId: string
  defaultDate: string
  onBack: () => void
  onFinish: () => void
}) {
  const shows = useCollection<Show>('shows', [where('tourId', '==', tour.id), orderBy('date')])
  // Opens on the tour's first day — the overwhelmingly common first show.
  const [draft, setDraft] = useState<ShowDraft>(() => initialShowDraft(null, defaultDate))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const patch = (next: Partial<ShowDraft>) => setDraft((previous) => ({ ...previous, ...next }))
  const valid = showIsValid(draft)

  async function save() {
    if (!valid) return
    setBusy(true)
    setError(null)
    try {
      await createShow(draft, tour.id, userId)
      // Reset for the next one and keep the date rolling forward, since dates
      // are the one field that is almost never the same twice.
      const next = new Date(`${draft.date}T00:00:00`)
      next.setDate(next.getDate() + 1)
      setDraft(initialShowDraft(null, toDateInputValue(next)))
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {shows.data.length > 0 && (
        <section className="stack" style={{ gap: 8 }}>
          <h2 className="t-eyebrow">On this tour</h2>
          <div className="row scroll-x" style={{ gap: 10 }}>
            {shows.data.map((show) => (
              <div
                key={show.id}
                className="panel stack"
                style={{ flex: 'none', gap: 2, padding: '14px 18px', minWidth: 170 }}
              >
                <span style={{ fontWeight: 600 }}>{show.city}</span>
                <span className="t-caption truncate">{show.venueName}</span>
                <span className="t-spec-caption faint">{formatShortDate(show.date.toDate())}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="stack" style={{ gap: 8 }}>
        <h2 className="t-eyebrow">{shows.data.length > 0 ? 'Add another show' : 'New show'}</h2>
        <div className="panel panel-pad stack" style={{ gap: 14 }}>
          <ShowFields draft={draft} patch={patch} autoFocus={false} />
          <ErrorText>{error}</ErrorText>
          <button
            className="btn btn-primary"
            disabled={!valid || busy}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save show'}
          </button>
        </div>
      </section>

      <StepFooter nextLabel="Go to tour" onBack={onBack} onSkip={onFinish} onNext={onFinish} />
    </>
  )
}
